import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import SlidePagePanel from '../mobile/SlidePagePanel'
import ConflictAlertCard from '../calendar/ConflictAlertCard'
import MorningBriefing from '../wellness/MorningBriefing'
import StressFootprintCard from './StressFootprintCard'
import DailyOverrideBanner from './DailyOverrideBanner'
import DecisionEvidencePanel from './DecisionEvidencePanel'
import MorningCheckIn from '../wellness/MorningCheckIn'
import { useMorningFlow } from '../../hooks/useMorningFlow'
import { hasWearableBiometricsToday } from '../../lib/morningFlow'
import {
  BRIEFING_KEY_CALENDAR_HINT,
  BRIEFING_KEY_STRESS_FOOTPRINT,
  briefingKeyMorning,
  isBriefingRead,
  markBriefingRead,
} from '../../lib/coachBriefingReadState'
import { countNewEvidenceReasons } from '../../lib/evidenceReadState'
import { useTodayProtocol } from '../../lib/useTodayProtocol'
import { resolveMorningBrief } from '../../lib/resolveMorningBrief'
import { clientLocalDate } from '../../lib/clientContext'
import { KILO } from '../../constants/KILO_THEME'
import type { AppliedEvidenceRule } from '../../lib/dailyOverrideTypes'
import type { CoachHealthSummary } from '../../lib/coachHealthTypes'
import type { ConflictAlert, SuggestedSlot } from '../../lib/conflicts'
import { SkeletonCard } from '../ui/Skeleton'

type CoachBriefingsCardProps = {
  summary: CoachHealthSummary | null
  loading: boolean
  conflict?: ConflictAlert | null
  forceOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onRescheduleConflict?: (slot: SuggestedSlot) => Promise<void>
  onDismissConflict?: () => Promise<void>
}

const CoachBriefingsCard = ({
  summary,
  loading,
  conflict,
  forceOpen = false,
  onOpenChange,
  onRescheduleConflict,
  onDismissConflict,
}: CoachBriefingsCardProps) => {
  const { t } = useTranslation()
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const [readVersion, setReadVersion] = useState(0)
  const [evidenceSeenVersion, setEvidenceSeenVersion] = useState(0)
  const [panelOpen, setPanelOpen] = useState(false)

  const calendarContext = summary?.calendar_stress_context
  const visibleMetrics = summary?.visible_metrics ?? []
  const todayId = clientLocalDate()
  const showCalendarHint =
    calendarContext?.calendar_connected &&
    !calendarContext.correlations_found &&
    (calendarContext.educational_message || calendarContext.unlock_hint)
  const showStressFootprint = Boolean(calendarContext?.correlations_found)
  const hasWearable = hasWearableBiometricsToday(summary)
  const showMorningBrief = visibleMetrics.includes('body_state_briefing')

  const { todayProtocol, overrideBusy, acceptOverride, rejectOverride, refresh } = useTodayProtocol(Boolean(summary))

  const morningBrief = useMemo(
    () => resolveMorningBrief(summary, todayProtocol, todayId),
    [summary, todayProtocol, todayId],
  )
  const hasMorningText = Boolean(morningBrief.text)
  const morningFlow = useMorningFlow({
    summary,
    todayProtocol,
    showMorningBrief,
    hasMorningText,
    onDispatched: () => { void refresh() },
  })

  const nonWearableBriefingActive =
    !morningFlow.hasWearable && morningFlow.activeStep === 'briefing'

  const hasPendingOverride = Boolean(todayProtocol?.has_pending_override && todayProtocol.override)

  const renderMorningCheckIn = morningFlow.showMorningCheckIn

  const renderMorningBriefing =
    !hasPendingOverride &&
    (morningFlow.activeStep === 'briefing' ||
      (morningFlow.activeStep === null &&
        morningFlow.hasWearable &&
        showMorningBrief &&
        hasMorningText &&
        morningFlow.briefingComplete))

  const evidenceApplied = useMemo((): AppliedEvidenceRule[] => {
    const raw = todayProtocol?.decision_trace?.evidence_applied
    return Array.isArray(raw) ? raw : []
  }, [todayProtocol?.decision_trace])

  const showEvidencePanel =
    evidenceApplied.length > 0 && !nonWearableBriefingActive && !hasPendingOverride

  const newEvidenceCount = useMemo(() => {
    void evidenceSeenVersion
    if (!showEvidencePanel) return 0
    return countNewEvidenceReasons(todayId, evidenceApplied)
  }, [evidenceSeenVersion, showEvidencePanel, todayId, evidenceApplied])

  const badgeCount = useMemo(() => {
    void readVersion
    let count = 0
    if ((showMorningBrief && hasMorningText || !hasWearable) && !isBriefingRead(briefingKeyMorning(todayId))) count += 1
    if (hasPendingOverride) count += 1
    if (newEvidenceCount > 0) count += 1
    if (showCalendarHint && !isBriefingRead(BRIEFING_KEY_CALENDAR_HINT)) count += 1
    if (showStressFootprint && !isBriefingRead(BRIEFING_KEY_STRESS_FOOTPRINT)) count += 1
    if (conflict) count += 1
    return count
  }, [
    readVersion,
    showMorningBrief,
    hasMorningText,
    hasWearable,
    todayId,
    hasPendingOverride,
    newEvidenceCount,
    showCalendarHint,
    showStressFootprint,
    conflict,
  ])

  const hasContent =
    showCalendarHint ||
    showMorningBrief ||
    morningFlow.activeStep != null ||
    morningFlow.showMorningCheckIn ||
    hasPendingOverride ||
    showStressFootprint ||
    Boolean(conflict)

  const teaser = useMemo(() => {
    if (conflict) return t('coach.sections.briefingsTeaserConflict')
    if (hasPendingOverride) return t('coach.sections.briefingsTeaserOverride')
    if (hasMorningText) return t('coach.briefing.title')
    if (!hasWearable && morningFlow.activeStep != null) return t('coach.briefing.nonWearableTitle')
    return null
  }, [conflict, hasPendingOverride, hasMorningText, hasWearable, morningFlow.activeStep, t])

  const setOpen = (next: boolean) => {
    setPanelOpen(next)
    onOpenChange?.(next)
  }

  useEffect(() => {
    if (forceOpen) {
      setPanelOpen(true)
      onOpenChange?.(true)
    }
  }, [forceOpen, onOpenChange])

  const markPassiveRead = () => {
    if ((showMorningBrief && hasMorningText) || !hasWearable) markBriefingRead(briefingKeyMorning(todayId))
    if (showCalendarHint) markBriefingRead(BRIEFING_KEY_CALENDAR_HINT)
    if (showStressFootprint) markBriefingRead(BRIEFING_KEY_STRESS_FOOTPRINT)
    setReadVersion((v) => v + 1)
  }

  const openPanel = () => {
    markPassiveRead()
    setOpen(true)
  }

  if (loading && !summary) {
    return (
      <div role="status">
        <SkeletonCard /></div>
    )
  }

  if (!summary || !hasContent) return null

  const title = t('coach.sections.briefings')

  return (
    <>
      <BentoCard gradient="none" className="!p-0 overflow-hidden">
        <button
          type="button"
          onClick={openPanel}
          className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-kilo-text/[0.02]"
          aria-expanded={panelOpen}
        >
          <MaterialIcon name="notifications" hoverable size="sm" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={KILO.meta}>{title}</p>
              {badgeCount > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-kilo-accent px-1.5 font-mono text-[10px] font-bold text-black">
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              ) : null}
            </div>
            {teaser ? (
              <p className={`mt-1 truncate text-sm leading-relaxed ${KILO.text.muted}`}>{teaser}</p>
            ) : null}
          </div>
          <MaterialIcon name="chevron_right" size="sm" className="shrink-0" />
        </button>
      </BentoCard>

      <SlidePagePanel open={panelOpen} onClose={() => setOpen(false)} title={title}>
        {hasPendingOverride && todayProtocol?.override ? (
          <DailyOverrideBanner
            briefingText={todayProtocol.override.briefing_text}
            workoutAdjustments={todayProtocol.override.workout_adjustments as Record<string, unknown>}
            onAccept={() => {
              setOverrideError(null)
              void acceptOverride().then((err) => {
                if (err) setOverrideError(err)
              })
            }}
            onReject={() => {
              setOverrideError(null)
              void rejectOverride().then((err) => {
                if (err) setOverrideError(err)
              })
            }}
            busy={overrideBusy}
          />
        ) : null}
        {overrideError ? <p className="text-sm text-red-400">{overrideError}</p> : null}

        {renderMorningCheckIn ? (
          <MorningCheckIn
            deferDispatch
            onPillarsComplete={morningFlow.completeCheckIn}
            dispatching={morningFlow.dispatching}
            dispatchError={morningFlow.dispatchError}
            onSubmitted={() => { void refresh() }}
            showSessionBanner={
              morningFlow.justCompletedThisSession &&
              morningFlow.checkInComplete &&
              morningFlow.activeStep !== 'check_in'
            }
            sessionCompletedAt={morningFlow.sessionCompletedAt}
            embedded
          />
        ) : null}

        {showEvidencePanel ? (
          <DecisionEvidencePanel
            evidence={evidenceApplied}
            embedded
            trackUpdates
            onSeenChange={() => setEvidenceSeenVersion((v) => v + 1)}
          />
        ) : null}

        {renderMorningBriefing ? (
          <MorningBriefing
            hasWearableData={morningFlow.hasWearable}
            briefingText={morningBrief.text}
            adjustments={morningBrief.adjustments}
            briefingDate={morningBrief.displayDate}
            isScheduledRest={morningBrief.isScheduledRest}
            showMorningBrief={showMorningBrief}
            hasMorningText={hasMorningText}
            awaitingAck={morningFlow.activeStep === 'briefing'}
            onAcknowledge={morningFlow.acknowledgeBriefing}
            onFinishBriefing={morningFlow.finishBriefing}
            dispatching={morningFlow.dispatching}
            nutritionSummary={summary.nutrition_yesterday ?? null}
            nutritionCoachNote={summary.nutrition_coach_note}
            nutritionFirstDay={summary.nutrition_first_day}
            evidenceApplied={evidenceApplied}
            pendingWellness={morningFlow.pendingPillars}
            embedded
          />
        ) : null}

        {showStressFootprint && calendarContext ? (
          <StressFootprintCard correlations={calendarContext.correlations} embedded />
        ) : null}

        {conflict && onRescheduleConflict && onDismissConflict ? (
          <ConflictAlertCard
            alert={conflict}
            onReschedule={onRescheduleConflict}
            onDismiss={onDismissConflict}
          />
        ) : null}

        {showCalendarHint && calendarContext ? (
          <div className={KILO.embeddedSection}>
            {calendarContext.educational_message ? (
              <p className={`text-sm ${KILO.text.primary}`}>{calendarContext.educational_message}</p>
            ) : null}
            {calendarContext.unlock_hint ? (
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                {calendarContext.unlock_hint}
              </p>
            ) : null}
          </div>
        ) : null}
      </SlidePagePanel>
    </>
  )
}

export default CoachBriefingsCard
